import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import { 
  Rocket, Target, Trophy, Zap, Users, Award, Heart, Shield, Star, 
  ChevronRight, Lightbulb, Crown, Quote, ArrowRight, Sparkles,
  CheckCircle, TrendingUp, Globe, Building2
} from 'lucide-react';
import omOssImage from '@/assets/om-oss-image.png';

const AboutUsDetailed = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Hero Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${omOssImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/90 to-background/95"></div>
        <div className="star-field"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 pt-12 sm:pt-16 md:pt-20">
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <Breadcrumbs />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
          
          {/* Epic Hero Section */}
          <div className="text-center mb-16 sm:mb-20 md:mb-24">
            <div className="inline-flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8 bg-gradient-to-r from-primary/20 to-accent/20 backdrop-blur-xl rounded-full px-6 sm:px-8 md:px-10 py-3 sm:py-4 border border-primary/30">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-primary animate-pulse" />
              <Badge variant="outline" className="bg-transparent border-primary/50 text-primary font-orbit text-base sm:text-lg px-4 sm:px-6">
                Revolusjonærer siden 1948
              </Badge>
            </div>
            
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-9xl font-orbit font-bold gradient-text mb-6 sm:mb-8 leading-none">
              PEVEO
            </h1>
            
            <div className="max-w-5xl mx-auto mb-8 sm:mb-12">
              <p className="text-xl sm:text-2xl md:text-3xl text-foreground font-semibold leading-tight mb-4 sm:mb-6">
                Bygger fremtidens treningsløsninger
              </p>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed px-4">
                Vi kombinerer <span className="text-primary font-bold">75 års erfaring</span> med 
                <span className="text-accent font-bold"> revolusjonær innovasjon</span> for å levere 
                produkter som faktisk endrer hvordan du trener.
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 px-4">
              <Badge className="bg-gradient-to-r from-primary to-accent text-primary-foreground px-4 sm:px-6 md:px-8 py-2 sm:py-3 text-sm sm:text-base md:text-lg">
                <Crown className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Norges laveste priser
              </Badge>
              <Badge variant="outline" className="border-accent text-accent px-4 sm:px-6 md:px-8 py-2 sm:py-3 text-sm sm:text-base md:text-lg">
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Norgesmester godkjent
              </Badge>
              <Badge variant="outline" className="border-secondary text-secondary px-4 sm:px-6 md:px-8 py-2 sm:py-3 text-sm sm:text-base md:text-lg">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Svensk kvalitet
              </Badge>
            </div>
          </div>

          {/* Story Timeline */}
          <div className="mb-16 sm:mb-20 md:mb-24">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-orbit font-bold text-center gradient-text mb-12 sm:mb-16">
              Vår Historie
            </h2>
            
            <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
              
              {/* 1948 - Foundation */}
              <Card className="peveo-card group hover:scale-105 transition-all duration-500 relative">
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-r from-accent to-secondary rounded-full flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-background" />
                </div>
                <CardHeader className="text-center pb-4">
                  <div className="text-4xl font-orbit font-bold text-accent mb-2">1948</div>
                  <CardTitle className="text-2xl font-orbit text-foreground">Grunnlagt i Sverige</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed text-center">
                    Startet som en liten fabrikk med fokus på kvalitet og presisjon. 
                    Grunnlaget for dagens suksess ble lagt med strenge kvalitetskrav.
                  </p>
                </CardContent>
              </Card>

              {/* Innovation */}
              <Card className="peveo-card group hover:scale-105 transition-all duration-500 relative">
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-background" />
                </div>
                <CardHeader className="text-center pb-4">
                  <div className="text-4xl font-orbit font-bold text-primary mb-2">2024</div>
                  <CardTitle className="text-2xl font-orbit text-foreground">Norsk Innovasjon</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed text-center">
                    Lanserte i Norge med revolusjonære treningsprodukter. 
                    Kombinerer svensk kvalitet med norsk innovasjonsånd.
                  </p>
                </CardContent>
              </Card>

              {/* Future */}
              <Card className="peveo-card group hover:scale-105 transition-all duration-500 relative">
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-r from-secondary to-primary rounded-full flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-background" />
                </div>
                <CardHeader className="text-center pb-4">
                  <div className="text-4xl font-orbit font-bold text-secondary mb-2">2025</div>
                  <CardTitle className="text-2xl font-orbit text-foreground">Fremtiden Starter</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed text-center">
                    Lanserer første treningsmaskin - Leg Extension 240kg. 
                    Starter revolusjon innen treningsteknologi.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Mission & Vision - Enhanced */}
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 mb-16 sm:mb-20 md:mb-24">
            
            {/* Mission */}
            <Card className="peveo-card group hover:scale-105 transition-all duration-700 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10"></div>
              <CardHeader className="relative">
                <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-primary/30 to-accent/30 rounded-full flex items-center justify-center group-hover:animate-pulse">
                  <Target className="w-12 h-12 text-primary" />
                </div>
                <CardTitle className="text-4xl font-orbit text-center gradient-text mb-6">
                  Vårt Oppdrag
                </CardTitle>
              </CardHeader>
              <CardContent className="relative space-y-8">
                
                <div className="bg-primary/15 p-8 rounded-2xl border border-primary/30 backdrop-blur-sm">
                  <Quote className="w-8 h-8 text-primary mb-4" />
                  <blockquote className="text-xl text-foreground italic leading-relaxed mb-4">
                    "Gjør som 2x Norgesmester Victor Bergman - velg kvalitet til riktig pris"
                  </blockquote>
                  <footer className="text-primary font-semibold">Victor Bergman, Norgesmester</footer>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground mb-2">Prisgaranti</h4>
                      <p className="text-muted-foreground">Vi garanterer Norges beste pris på sammenlignbare produkter</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground mb-2">Kvalitetsløfte</h4>
                      <p className="text-muted-foreground">Hver batch testet og verifisert for din sikkerhet</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground mb-2">Transparens</h4>
                      <p className="text-muted-foreground">Åpenhet om ingredienser, produksjon og forskning</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="peveo-card group hover:scale-105 transition-all duration-700 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-secondary/10"></div>
              <CardHeader className="relative">
                <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-accent/30 to-secondary/30 rounded-full flex items-center justify-center group-hover:animate-pulse">
                  <Rocket className="w-12 h-12 text-accent" />
                </div>
                <CardTitle className="text-4xl font-orbit text-center gradient-text mb-6">
                  Vår Visjon
                </CardTitle>
              </CardHeader>
              <CardContent className="relative space-y-8">
                
                <p className="text-xl text-foreground leading-relaxed text-center">
                  Revolusjonere norsk treningskultur ved å bygge maskiner som faktisk fungerer optimalt for kroppen.
                </p>

                <div className="bg-accent/15 p-8 rounded-2xl border border-accent/30 backdrop-blur-sm">
                  <h3 className="text-2xl font-orbit font-bold text-accent mb-4 text-center">
                    Første Innovasjon: Leg Extension 240kg
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <ArrowRight className="w-5 h-5 text-accent" />
                      <span className="text-foreground">Justerbar hoftefleksjon for optimal ROM</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <ArrowRight className="w-5 h-5 text-accent" />
                      <span className="text-foreground">Biommekanisk optimert motstandsprofil</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <ArrowRight className="w-5 h-5 text-accent" />
                      <span className="text-foreground">240kg kapasitet for maksimal progresjon</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <ArrowRight className="w-5 h-5 text-accent" />
                      <span className="text-foreground">Basert på forskning og real-world testing</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Impact Metrics */}
          <div className="bg-gradient-to-r from-primary/15 via-accent/10 to-secondary/15 rounded-3xl p-8 sm:p-12 md:p-16 mb-16 sm:mb-20 md:mb-24 border border-primary/20 backdrop-blur-xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-orbit font-bold text-center gradient-text mb-12 sm:mb-16">
              Påvirkning i Tall
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-12">
              <div className="text-center group">
                <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-primary mx-auto mb-3 sm:mb-4 group-hover:animate-pulse" />
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary mb-2 sm:mb-3 font-orbit">240kg</div>
                <p className="text-xs sm:text-sm md:text-base text-muted-foreground font-medium">Maksimal vekt i første maskin</p>
              </div>
              
              <div className="text-center group">
                <Users className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-accent mx-auto mb-3 sm:mb-4 group-hover:animate-pulse" />
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-accent mb-2 sm:mb-3 font-orbit">200+</div>
                <p className="text-xs sm:text-sm md:text-base text-muted-foreground font-medium">Produkttestere og utøvere</p>
              </div>
              
              <div className="text-center group">
                <Globe className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-secondary mx-auto mb-3 sm:mb-4 group-hover:animate-pulse" />
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary mb-2 sm:mb-3 font-orbit">75</div>
                <p className="text-xs sm:text-sm md:text-base text-muted-foreground font-medium">År med kvalitetsproduksjon</p>
              </div>
              
              <div className="text-center group">
                <Crown className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-primary mx-auto mb-3 sm:mb-4 group-hover:animate-pulse" />
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary mb-2 sm:mb-3 font-orbit">100%</div>
                <p className="text-xs sm:text-sm md:text-base text-muted-foreground font-medium">Prisgaranti på alle produkter</p>
              </div>
            </div>
          </div>

          {/* Values in Detail */}
          <div className="mb-16 sm:mb-20 md:mb-24">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-orbit font-bold text-center gradient-text mb-12 sm:mb-16">
              Våre Kjerneverdier
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              
              {/* Quality */}
              <Card className="peveo-card group hover:scale-105 transition-all duration-500 h-full">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 mx-auto mb-8 bg-gradient-to-br from-secondary/30 to-primary/30 rounded-full flex items-center justify-center group-hover:animate-pulse">
                    <Shield className="w-10 h-10 text-secondary" />
                  </div>
                  <h3 className="text-2xl font-orbit font-bold text-secondary mb-6">Kvalitetsgaranti</h3>
                  
                  <div className="space-y-4 text-left">
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-secondary mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Produsert i Sverige siden 1948</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-secondary mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Hver batch laboratorietestet</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-secondary mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">100% dopingfri garanti</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-secondary mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Sporbarhet fra råvare til ferdig produkt</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Innovation */}
              <Card className="peveo-card group hover:scale-105 transition-all duration-500 h-full">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 mx-auto mb-8 bg-gradient-to-br from-primary/30 to-accent/30 rounded-full flex items-center justify-center group-hover:animate-pulse">
                    <Zap className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="text-2xl font-orbit font-bold text-primary mb-6">Smaker til Perfeksjon</h3>
                  
                  <div className="space-y-4 text-left">
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Fokus på én perfekt smak fremfor mange middelmådige</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Citrus variant vant blant 200+ testere</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span className="text-muted-foregor">Kontinuerlig smaksforbedring basert på tilbakemeldinger</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Naturlige ingredienser uten kunstige tilsetningsstoffer</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Social Impact */}
              <Card className="peveo-card group hover:scale-105 transition-all duration-500 h-full md:col-span-2 lg:col-span-1">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 mx-auto mb-8 bg-gradient-to-br from-accent/30 to-secondary/30 rounded-full flex items-center justify-center group-hover:animate-pulse">
                    <Heart className="w-10 h-10 text-accent" />
                  </div>
                  <h3 className="text-2xl font-orbit font-bold text-accent mb-6">For en God Sak</h3>
                  
                  <div className="space-y-4 text-left">
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-accent mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Overskudd finansierer kneforskningsprosjekter</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-accent mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Samarbeid med rehabiliteringssentre</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-accent mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Støtter idrettsutøvere med skader</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-accent mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">Du investerer i fremtiden for bedre trening</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Final Epic CTA */}
          <div className="text-center bg-gradient-to-br from-primary/20 via-accent/15 to-secondary/20 rounded-3xl p-20 backdrop-blur-xl border border-primary/30 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-accent/5"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-6 mb-12">
                <Sparkles className="w-16 h-16 text-primary animate-pulse" />
                <h2 className="text-7xl font-orbit font-bold gradient-text">PEVEO</h2>
                <Sparkles className="w-16 h-16 text-primary animate-pulse" />
              </div>
              
              <p className="text-3xl text-foreground leading-relaxed max-w-4xl mx-auto mb-12 font-semibold">
                Revolusjonerer norsk trening - én vitenskapelig innovasjon av gangen
              </p>
              
              <div className="flex flex-wrap justify-center gap-6">
                <Badge className="bg-gradient-to-r from-primary to-accent text-primary-foreground px-12 py-4 text-xl">
                  <Trophy className="w-6 h-6 mr-3" />
                  Fremtidens treningspartner
                </Badge>
                <Badge variant="outline" className="border-primary text-primary px-12 py-4 text-xl backdrop-blur-sm">
                  <Star className="w-6 h-6 mr-3" />
                  Kvalitet du kan stole på
                </Badge>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    </div>
  );
};

export default AboutUsDetailed;